
import { Component, Vue } from 'vue-property-decorator'
import {
  AimGroup,
  listAimGroups,
  createAimGroup,
  addStudentsToAimGroup,
  removeStudentFromAimGroup
} from '@/api/aim'
import { AdminApi } from '@/wwapi/apis/admin-api'
import { GetUsersResponse, UserModel } from '@/wwapi/models'

interface UserOption {
  userId: string
  label: string
}

@Component({
  name: 'TrainerTrainee'
})
export default class extends Vue {
  private groups: AimGroup[] = []
  private listLoading = false
  private submitting = false

  private teacherId = ''
  private studentIds: string[] = []
  private teacherOptions: UserOption[] = []
  private studentOptions: UserOption[] = []

  private editDialogVisible = false
  private editingGroup: AimGroup | null = null
  private editStudentIds: string[] = []
  private editSubmitting = false

  created() {
    this.fetchGroups()
  }

  private async fetchGroups() {
    this.listLoading = true
    try {
      const response = await listAimGroups()
      const data = response.data
      if (data?.status?.code === 'success' && Array.isArray(data?.data)) {
        this.groups = data.data
      } else if (Array.isArray(data?.data)) {
        this.groups = data.data
      } else {
        this.groups = []
      }
    } catch (error) {
      console.error('Failed to load AIM groups', error)
      this.$notify({
        title: 'Error',
        message: 'Failed to load AIM groups',
        type: 'error',
        duration: 3000
      })
    } finally {
      this.listLoading = false
    }
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  private resetForm() {
    this.teacherId = ''
    this.studentIds = []
    this.teacherOptions = []
    this.studentOptions = []
  }

  private async submitGroup() {
    if (!this.teacherId) {
      this.$notify({ title: 'Validation', message: 'Select a teacher', type: 'warning' })
      return
    }
    if (!this.studentIds.length) {
      this.$notify({ title: 'Validation', message: 'Select at least one student', type: 'warning' })
      return
    }
    this.submitting = true
    try {
      const response = await createAimGroup({
        teacherId: this.teacherId,
        studentIds: this.studentIds
      })
      const body = response.data
      if (body?.status?.code === 'success') {
        this.$notify({
          title: 'Success',
          message: `AIM group saved (${this.studentIds.length} students added)`,
          type: 'success',
          duration: 3000
        })
        this.resetForm()
        await this.fetchGroups()
      } else {
        this.$notify({
          title: 'Error',
          message: body?.status?.message || 'Failed to save group',
          type: 'error'
        })
      }
    } catch (err: any) {
      this.$notify({
        title: 'Error',
        message: err?.message || 'Failed to save group',
        type: 'error'
      })
    } finally {
      this.submitting = false
    }
  }

  private openEditGroup(group: AimGroup) {
    this.editingGroup = group
    this.editStudentIds = []
    this.studentOptions = []
    this.editDialogVisible = true
  }

  private async submitAddStudents() {
    if (!this.editingGroup || !this.editStudentIds.length) {
      this.editDialogVisible = false
      return
    }
    this.editSubmitting = true
    try {
      const response = await addStudentsToAimGroup(
        this.editingGroup.teacherId,
        this.editStudentIds
      )
      if (response.data?.status?.code === 'success') {
        this.$notify({
          title: 'Success',
          message: 'Students added to the group',
          type: 'success'
        })
        this.editDialogVisible = false
        this.editingGroup = null
        this.editStudentIds = []
        await this.fetchGroups()
      } else {
        this.$notify({
          title: 'Error',
          message: response.data?.status?.message || 'Failed to add students',
          type: 'error'
        })
      }
    } catch (err: any) {
      this.$notify({
        title: 'Error',
        message: err?.message || 'Failed to add students',
        type: 'error'
      })
    } finally {
      this.editSubmitting = false
    }
  }

  private async handleRemoveStudent(group: AimGroup, student: { userId: string, email?: string }) {
    try {
      await this.$confirm(
        `Remove ${student.email || student.userId} from ${group.teacherEmail || group.teacherId}'s group?`,
        'Confirm',
        {
          type: 'warning',
          confirmButtonText: 'Remove',
          cancelButtonText: 'Cancel'
        }
      )
    } catch {
      return
    }
    try {
      const response = await removeStudentFromAimGroup(group.teacherId, student.userId)
      if (response.data?.status?.code === 'success') {
        this.$notify({
          title: 'Success',
          message: 'Student removed from group',
          type: 'success'
        })
        await this.fetchGroups()
      } else {
        this.$notify({
          title: 'Error',
          message: response.data?.status?.message || 'Failed to remove student',
          type: 'error'
        })
      }
    } catch (err: any) {
      this.$notify({
        title: 'Error',
        message: err?.message || 'Failed to remove student',
        type: 'error'
      })
    }
  }

  private async searchTeacher(name: string) {
    if (!name || name.length < 3) return
    const users = await this.doUserSearch(name)
    this.teacherOptions = users
  }

  private async searchStudents(name: string) {
    if (!name || name.length < 3) return
    const users = await this.doUserSearch(name)
    this.studentOptions = users
  }

  private async doUserSearch(name: string): Promise<UserOption[]> {
    try {
      const adminApi = new AdminApi()
      const response = await adminApi.userListPost({ text: name })
      const body: GetUsersResponse = response.data
      const users: UserModel[] = body.data ?? []
      return users.map((u: any) => ({
        userId: u.userId,
        label: `${(u.firstName ?? '').trim()} ${(u.lastName ?? '').trim()} (${u.email ?? u.userId})`.trim()
      }))
    } catch (err) {
      console.error('User search failed', err)
      return []
    }
  }
}
